import { Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';
import { forkJoin, Observable, of, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';

import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { Events } from 'src/providers/events/events';
import { Me } from 'src/shared/providers/me';
import { Team } from 'src/providers/team/team';
import { InAppBrowserUtils } from 'src/shared/utils/in-app-browser-utils';
import { ThemeService } from 'src/shared/services/theme.service';

import { desktopMenuItems } from 'src/shared/components/bottom-nav/bottom-nav-items';

@Component({
    selector: 'app-desktop-nav',
    templateUrl: './desktop-nav.component.html',
    styleUrls: ['./desktop-nav.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class DesktopNavComponent implements OnInit, OnDestroy {

  userName$: Observable<string>;
  teamName$: Observable<string>;
  userName = '';
  initials = '';
  showUserDropdown = false;
  helpMenuExpanded = false;
  menuItems = desktopMenuItems;

  private routerSubscription: Subscription;
  private navDataSubscription: Subscription;

  @ViewChild('userIconRef') userIconRef: ElementRef;
  @ViewChild('userDropdownRef') userDropdownRef: ElementRef;

  constructor(
    private customNavCtrl: CustomNavController,
    private events: Events,
    private iabUtils: InAppBrowserUtils,
    private me: Me,
    public router: Router,
    private team: Team,
    public themeService: ThemeService
  ) {}

  ngOnInit() {
    this.loadNavData();
    this.events.subscribe('auth:authenticated', () => this.loadNavData());
    this.events.subscribe('team-details:updated', ({ details }) => this.teamDetailsUpdated(details));
  }

  ngOnDestroy() {
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
    if (this.navDataSubscription) {
      this.navDataSubscription.unsubscribe();
    }
  }

  loadNavData() {
    this.userName$ = forkJoin([this.me.fetchWithoutCache()]).pipe(
      map(data => {
        const name = data[0]?.profile?.name;
        if (name) {
          this.userName = this.getFirstName(name);
          this.initials = this.parseInitials(name);
        }
        return name;
      }),
    );

    // The template reads the plain `userName`/`initials` properties (not the
    // async pipe), which are populated as a side effect of the map above. That
    // map only runs when the observable is subscribed, so subscribe here —
    // otherwise the header always falls back to "P"/"Pro". See Issue 25.
    this.navDataSubscription?.unsubscribe();
    this.navDataSubscription = this.userName$.subscribe();

    this.teamName$ = forkJoin([this.team.getMyTeam()]).pipe(
      map(data => data[0]?.name)
    );
  }

  teamDetailsUpdated(details) {
    this.teamName$ = of(details.name);
  }

  isCurrentItem(item: { target: string }): boolean {
    if (item.target === '/dashboard') {
      return this.router.url === '/' || this.router.url === '/dashboard';
    }
    return this.router.url === item.target || this.router.url.startsWith(item.target + '/');
  }

  goToLink(link: string) {
    this.customNavCtrl.navigateForward(link);
  }

  parseInitials(fullName: string): string {
    if (!fullName) return '';
    return fullName
      .match(/(\b\S)?/g)
      .join('')
      .match(/(^\S|\S$)?/g)
      .join('')
      .toUpperCase();
  }

  getFirstName(fullName: string): string {
    if (!fullName) return '';
    return fullName.split(' ')[0];
  }

  toggleUserDropdown(event: Event) {
    event.stopPropagation();
    this.showUserDropdown = !this.showUserDropdown;
  }

  toggleHelpMenu(event: Event) {
    event.stopPropagation();
    this.helpMenuExpanded = !this.helpMenuExpanded;
  }

  @HostListener('document:click', ['$event'])
  clickOutside(event: Event) {
    if (
      this.showUserDropdown &&
      this.userIconRef?.nativeElement &&
      this.userDropdownRef?.nativeElement &&
      !this.userIconRef.nativeElement.contains(event.target) &&
      !this.userDropdownRef.nativeElement.contains(event.target)
    ) {
      this.showUserDropdown = false;
    }
  }

  goToSettings() {
    this.showUserDropdown = false;
    this.customNavCtrl.navigateForward('my-account');
  }

  logout(): void {
    this.showUserDropdown = false;
    this.events.publish('auth:logout');
  }

  openDocumentation() {
    this.iabUtils.openUrl('https://help.tidy.com/pros');
  }

  openTerms() {
    this.iabUtils.openUrl('https://www.tidy.com/legal/pro');
  }

  openWhatsNew() {
    this.iabUtils.openUrl('https://www.tidy.com/blog?tag=changelog');
  }

  openContactSupport() {
    this.iabUtils.openUrl('https://www.tidy.com/contact');
  }
}
